import React, { useEffect, useState } from "react";
import { Plus, Repeat, Users, Image as ImageIcon, X } from "react-feather";
import { useTheme } from "../theme/ThemeContext";
import { useTasks } from "../tasks/useTasks";
import { useGroups } from "../groups/useGroups";
import { useRealtime } from "../realtime/useRealtime";
import { initialSharing, sharingBody } from "../tasks/sharing";
import { REPEAT_OPTIONS, repeatOptionLabel, localToday, repeatLabel, formatDue, dueText, taskBucket, primaryAction } from "../tasks/taskUtils";
import PhotoField from "../tasks/PhotoField";

const roleWord = { admin: "Admin", doer: "Doer" };

const TaskModal = ({ task, api, onClose }) => {
  const { tokens, fonts } = useTheme();
  const editing = !!task;
  const [form, setForm] = useState({
    title: task ? task.title : "",
    description: task ? task.description : "",
    repeatUnit: task ? task.repeatUnit : "once",
    repeatEvery: task ? String(task.repeatEvery) : "1",
    dueDate: task ? task.dueDate : localToday(),
    dueTime: task && task.dueTime ? task.dueTime : "",
  });
  const [sharing, setSharing] = useState(() => initialSharing(task));
  const groupsApi = useGroups();
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // The task list only carries a `hasPhoto` flag (kept light); the actual image is
  // fetched only when opening the edit form, and only if there is one to fetch.
  const [photo, setPhoto] = useState(null);
  const [photoTouched, setPhotoTouched] = useState(false);
  useEffect(() => {
    if (task && task.hasPhoto) api.details(task.id).then((d) => setPhoto(d.photo)).catch(() => {});
  }, [task, api]);
  const changePhoto = (value) => {
    setPhoto(value);
    setPhotoTouched(true);
  };

  const field = { width: "100%", boxSizing: "border-box", padding: "9px 12px", fontSize: 14, borderRadius: 8, border: `1px solid ${tokens.surfaceBorder}`, backgroundColor: tokens.bg, color: tokens.textPrimary, fontFamily: "inherit" };
  const label = { display: "block", marginBottom: 4, fontSize: 12, fontWeight: 600, color: tokens.textMuted };

  const available = groupsApi.contacts.filter((c) => !sharing.people.includes(c.username));

  // Picking a name adds them straight away; nothing else to click.
  const addPeople = (usernames) => {
    setError("");
    setSharing((s) => ({ ...s, people: [...s.people, ...usernames.filter((u) => !s.people.includes(u))] }));
  };
  const removePerson = (username) =>
    setSharing((s) => ({ ...s, people: s.people.filter((p) => p !== username), adminWho: s.adminWho === username ? "me" : s.adminWho }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return setError("Give the task a name.");
    if (sharing.audience === "others" && sharing.people.length === 0) return setError("Choose who this task is for.");
    setSaving(true);
    setError("");
    const body = {
      title: form.title,
      description: form.description,
      repeatUnit: form.repeatUnit,
      repeatEvery: form.repeatUnit === "once" ? 1 : parseInt(form.repeatEvery, 10) || 1,
      dueDate: form.dueDate,
      dueTime: form.dueTime || null,
      ...sharingBody(sharing),
    };
    // Only sent when the user actually touched the photo field, so an unrelated
    // edit (renaming, changing the date) never re-uploads the same image.
    if (!editing || photoTouched) body.photo = photo;
    try {
      if (editing) await api.update(task.id, body);
      else await api.create(body);
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const segBtn = (value, text) => (
    <button
      type="button"
      onClick={() => setSharing((s) => ({ ...s, audience: value }))}
      style={{ flex: 1, padding: "9px 8px", border: "none", borderRadius: 7, cursor: "pointer", fontSize: 13, fontWeight: 700, backgroundColor: sharing.audience === value ? tokens.surface : "transparent", color: sharing.audience === value ? tokens.textPrimary : tokens.textMuted, boxShadow: sharing.audience === value ? tokens.cardShadow : "none" }}
    >
      {text}
    </button>
  );

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 3000, backgroundColor: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} style={{ width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", boxSizing: "border-box", padding: 24, borderRadius: 14, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontFamily: fonts.display, color: tokens.textPrimary }}>{editing ? "Edit task" : "New task"}</h3>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", color: tokens.textMuted }}><X size={20} /></button>
        </div>

        <label style={label}>Task name</label>
        <input style={{ ...field, marginBottom: 12 }} value={form.title} onChange={set("title")} maxLength={100} autoFocus />
        <label style={label}>Details (optional)</label>
        <textarea style={{ ...field, minHeight: 64, marginBottom: 12, resize: "vertical" }} value={form.description} onChange={set("description")} />
        <PhotoField tokens={tokens} label="Photo (optional) — shows exactly what needs doing" value={photo} onChange={changePhoto} />

        <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
          {/* minWidth: 0 stops a native time input (wide on iOS) from pushing past its share of the row */}
          <div style={{ flex: 1, minWidth: 0 }}><label style={label}>Date</label><input style={field} type="date" value={form.dueDate} onChange={set("dueDate")} /></div>
          <div style={{ flex: 1, minWidth: 0 }}><label style={label}>Time (optional)</label><input style={field} type="time" value={form.dueTime} onChange={set("dueTime")} /></div>
        </div>
        <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={label}>Repeats</label>
            <select style={field} value={form.repeatUnit} onChange={set("repeatUnit")}>
              {REPEAT_OPTIONS.map((o) => <option key={o.unit} value={o.unit}>{repeatOptionLabel(o)}</option>)}
            </select>
          </div>
          {form.repeatUnit !== "once" && (
            <div style={{ width: 100 }}><label style={label}>Every</label><input style={field} type="number" min="1" max="365" value={form.repeatEvery} onChange={set("repeatEvery")} /></div>
          )}
        </div>

        <div style={{ fontSize: 13, fontWeight: 700, color: tokens.textPrimary, marginBottom: 8 }}>Who is this for?</div>
        <div style={{ display: "flex", padding: 4, borderRadius: 10, backgroundColor: tokens.neutralSoft, marginBottom: 14 }}>
          {segBtn("me", "Just me")}
          {segBtn("others", "Other people")}
        </div>

        {sharing.audience === "others" && (
          groupsApi.contacts.length === 0 ? (
            <p style={{ margin: "0 0 16px", fontSize: 13, color: tokens.textMuted }}>To share a task, first add people to a group in Settings → Groups.</p>
          ) : (
            <>
              {sharing.people.length > 0 && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                  {sharing.people.map((u) => (
                    <span key={u} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 6px 5px 12px", borderRadius: 999, fontSize: 13, fontWeight: 600, backgroundColor: tokens.accentSoft, color: tokens.textPrimary }}>
                      {u}
                      <button type="button" onClick={() => removePerson(u)} aria-label={`Remove ${u}`} style={{ display: "flex", padding: 2, border: "none", borderRadius: 999, background: "none", cursor: "pointer", color: tokens.textMuted }}><X size={14} /></button>
                    </span>
                  ))}
                </div>
              )}

              {available.length > 0 && (
                <select style={{ ...field, marginBottom: 8 }} value="" onChange={(e) => e.target.value && addPeople([e.target.value])}>
                  <option value="">{sharing.people.length ? "Add another person…" : "Choose a person…"}</option>
                  {available.map((c) => <option key={c.userId} value={c.username}>{c.username}{c.email ? ` (${c.email})` : ""}</option>)}
                </select>
              )}

              {available.length > 0 && groupsApi.groups.filter((g) => g.members.length > 0).length > 0 && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                  {groupsApi.groups.filter((g) => g.members.length > 0).map((g) => (
                    <button key={g.id} type="button" onClick={() => addPeople(g.members.map((m) => m.username))} style={{ padding: "5px 12px", borderRadius: 999, border: `1px solid ${tokens.surfaceBorder}`, background: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, color: tokens.textMuted }}>
                      + Everyone in {g.name}
                    </button>
                  ))}
                </div>
              )}

              {sharing.people.length > 0 && (
                <>
                  <label style={label}>Who confirms it's done?</label>
                  <select style={{ ...field, marginBottom: 6 }} value={sharing.adminWho} onChange={(e) => setSharing((s) => ({ ...s, adminWho: e.target.value }))}>
                    <option value="me">Me</option>
                    {sharing.people.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                  <p style={{ margin: "0 0 16px", fontSize: 12, color: tokens.textMuted }}>Everyone else just clicks "I've done it", and then waits for the confirmation.</p>
                </>
              )}
            </>
          )
        )}

        {error && <div role="alert" style={{ marginBottom: 12, fontSize: 13, color: tokens.negative }}>{error}</div>}
        <button type="submit" disabled={saving} style={{ width: "100%", padding: "11px 16px", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", backgroundColor: tokens.accent, color: tokens.accentContrast, opacity: saving ? 0.6 : 1 }}>
          {saving ? "Saving…" : editing ? "Save changes" : "Add task"}
        </button>
      </form>
    </div>
  );
};
// A doer marking a task done, with an optional proof photo and note - a small
// modal in the same style as TaskModal, rather than a plain confirm button.
const SubmitModal = ({ task, api, onClose }) => {
  const { tokens, fonts } = useTheme();
  const [photo, setPhoto] = useState(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const field = { width: "100%", boxSizing: "border-box", padding: "9px 12px", fontSize: 14, borderRadius: 8, border: `1px solid ${tokens.surfaceBorder}`, backgroundColor: tokens.bg, color: tokens.textPrimary, fontFamily: "inherit" };

  const send = async () => {
    setSaving(true);
    setError("");
    try {
      await api.submit(task.id, note.trim(), photo);
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 3000, backgroundColor: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 440, boxSizing: "border-box", padding: 24, borderRadius: 14, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontFamily: fonts.display, color: tokens.textPrimary }}>Mark as done</h3>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", color: tokens.textMuted }}><X size={20} /></button>
        </div>
        {!!task.description && <p style={{ margin: "0 0 14px", fontSize: 13, color: tokens.textMuted }}>{task.description}</p>}
        <PhotoField tokens={tokens} label="Photo (optional) — show it's done" value={photo} onChange={setPhoto} />
        <label style={{ display: "block", marginBottom: 4, fontSize: 12, fontWeight: 600, color: tokens.textMuted }}>Note (optional)</label>
        <input style={{ ...field, marginBottom: 16 }} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything the admin should know" />
        {error && <div role="alert" style={{ marginBottom: 12, fontSize: 13, color: tokens.negative }}>{error}</div>}
        <button type="button" disabled={saving} onClick={send} style={{ width: "100%", padding: "11px 16px", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", backgroundColor: tokens.accent, color: tokens.accentContrast, opacity: saving ? 0.6 : 1 }}>
          {saving ? "Sending…" : "I've done it"}
        </button>
      </div>
    </div>
  );
};

const Tasks = () => {
  const { tokens, fonts } = useTheme();
  const api = useTasks();
  const [modal, setModal] = useState(null); // null | {} | task
  const [submittingTask, setSubmittingTask] = useState(null);
  const [message, setMessage] = useState("");
  const [alertText, setAlertText] = useState("");

  // Live updates: refresh straight away when someone else changes a task.
  const reloadTasks = api.reload;
  useRealtime((event) => {
    reloadTasks();
    if (event.message) setAlertText(event.message);
  });

  const run = (fn) => {
    setMessage("");
    fn().catch((err) => setMessage(err.message));
  };

  const buckets = { action: [], later: [], others: [], done: [] };
  for (const t of api.tasks) {
    const b = taskBucket(t);
    if (b === "todo" || b === "confirm") buckets.action.push(t);
    else if (b === "later") buckets.later.push(t);
    else if (b === "done") buckets.done.push(t);
    else buckets.others.push(t);
  }

  const btn = (kind) => ({
    padding: "7px 14px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    border: kind === "primary" ? "none" : `1px solid ${kind === "danger" ? tokens.negative : tokens.surfaceBorder}`,
    backgroundColor: kind === "primary" ? tokens.accent : "transparent",
    color: kind === "primary" ? tokens.accentContrast : kind === "danger" ? tokens.negative : tokens.textPrimary,
  });

  const Row = ({ task }) => {
    const action = primaryAction(task);
    const overdue = task.state === "open" && task.dueDate < localToday();
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 0", borderTop: `1px solid ${tokens.surfaceBorder}` }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: tokens.textPrimary }}>{task.title}</div>
            {task.hasPhoto && <ImageIcon size={14} color={tokens.textMuted} title="Has a reference photo - open Edit to see it" />}
          </div>
          {!!task.description && <div style={{ fontSize: 13, color: tokens.textMuted, marginTop: 2 }}>{task.description}</div>}
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 6, fontSize: 12, color: overdue ? tokens.negative : tokens.textMuted, fontWeight: overdue ? 700 : 400 }}>
            <span>{dueText(task)} · {formatDue(task)}</span>
            {task.repeatUnit !== "once" && <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}><Repeat size={12} />{repeatLabel(task)}</span>}
            {task.members.length > 1 && (
              <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                <Users size={12} />{task.members.map((m) => `${m.username} (${roleWord[m.role]})`).join(", ")}
              </span>
            )}
            <span>You: {roleWord[task.myRole]}</span>
          </div>
          {task.state === "submitted" && (
            <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: tokens.pending }}>
              {task.myRole === "admin" ? `${task.lastEvent ? task.lastEvent.username : "Someone"} says it's done. Confirm?` : "Waiting for an admin to confirm"}
            </div>
          )}
          {task.state === "open" && task.lastEvent && task.lastEvent.kind === "rejected" && (
            <div style={{ marginTop: 6, fontSize: 12, color: tokens.pending }}>Sent back by {task.lastEvent.username}{task.lastEvent.note ? `: "${task.lastEvent.note}"` : ""}</div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {action && <button style={btn("primary")} onClick={() => (action.kind === "submit" ? setSubmittingTask(task) : run(() => api.confirm(task.id)))}>{action.label}</button>}
          {task.myRole === "admin" && task.state === "submitted" && <button style={btn("ghost")} onClick={() => run(() => api.reject(task.id))}>Send back</button>}
          {task.isOwner && <button style={btn("ghost")} onClick={() => setModal(task)}>Edit</button>}
          <button
            style={btn("danger")}
            onClick={() => {
              const question = task.isOwner ? `Delete "${task.title}" for everyone?` : `Remove "${task.title}" from your tasks? ${task.ownerName} keeps it.`;
              if (window.confirm(question)) run(() => api.remove(task.id));
            }}
          >
            Delete
          </button>
        </div>
      </div>
    );
  };

  const Section = ({ title, items }) =>
    items.length === 0 ? null : (
      <div style={{ marginBottom: 20, padding: "14px 20px", borderRadius: 14, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
        <h3 style={{ margin: "0 0 4px", fontFamily: fonts.display, fontSize: 15, color: tokens.textPrimary }}>{title}</h3>
        {items.map((t) => <Row key={t.id} task={t} />)}
      </div>
    );

  return (
    <div style={{ padding: "0 24px 40px", maxWidth: 900, margin: "0 auto", fontFamily: fonts.body }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, fontFamily: fonts.display, color: tokens.textPrimary }}>Tasks</h2>
          <p style={{ margin: "4px 0 0", fontSize: 14, color: tokens.textMuted }}>Reminders that can repeat, and can be shared with someone who confirms them.</p>
        </div>
        <button onClick={() => setModal({})} style={{ ...btn("primary"), display: "flex", alignItems: "center", gap: 6, padding: "10px 16px" }}><Plus size={16} /> New task</button>
      </div>

      {alertText && (
        <div role="status" onClick={() => setAlertText("")} style={{ marginBottom: 14, padding: 12, borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer", backgroundColor: tokens.accentSoft, color: tokens.textPrimary }}>
          {alertText}
        </div>
      )}
      {(message || api.error) && (
        <div role="alert" style={{ marginBottom: 14, padding: 12, borderRadius: 10, fontSize: 13, backgroundColor: tokens.pendingBg, color: tokens.pending }}>
          {message || "Couldn't reach the server."}
        </div>
      )}
      {api.loading && <p style={{ color: tokens.textMuted }}>Loading…</p>}
      {!api.loading && api.tasks.length === 0 && <p style={{ color: tokens.textMuted }}>No tasks yet. Click "New task" to add your first reminder.</p>}

      <Section title="Needs you now" items={buckets.action} />
      <Section title="Coming up" items={buckets.later} />
      <Section title="With others" items={buckets.others} />
      <Section title="Completed" items={buckets.done} />

      {modal && <TaskModal task={modal.id ? modal : null} api={api} onClose={() => setModal(null)} />}
      {submittingTask && <SubmitModal task={submittingTask} api={api} onClose={() => setSubmittingTask(null)} />}
    </div>
  );
};

export default Tasks;
